import { makeInputContext } from '@objectiv/schema';
import { useInputChangeEventTracker } from '@objectiv/tracker-react';
import clsx from 'clsx';
import { forwardRef } from 'react';
import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';

import { textMd, textMdSemibold } from '../../typography';

import styles from './IntlPhoneInput.module.css';

import type { PhoneInputProps, PhoneInputRefType } from 'react-international-phone';

interface ExtendedPhoneInputProps extends PhoneInputProps {
  error?: string;
  label?: string;
  internalInputRef?: React.RefObject<HTMLInputElement>;
  removeLeadingZero?: boolean;
  id?: string;
  wrapperClassName?: string;
  bottomHint?: string;
  hasNoBottomContent?: boolean; // To show/hide the hint/error container below which creates a white space
  trackingId?: string;
}

export const IntlPhoneInput = forwardRef<PhoneInputRefType, ExtendedPhoneInputProps>(
  ({ removeLeadingZero = true, hasNoBottomContent = false, ...props }, ref) => {
    const trackingId = props.trackingId ?? props.id ?? 'phone-number';
    /** Since the PhoneInput component handles input events internally, we cannot pass this component directly to Objectiv's TrackedInputContext.
     * Instead, we use the useInputChangeEventTracker hook to track input onBlur (same as in TrackedInputContext, regular text inputs emits Objectiv event onBlur by default)
     * We do not track actual value changes, because phone number is PII
     */

    const inputChangeEventTracker = useInputChangeEventTracker({
      locationStack: [makeInputContext({ id: trackingId })],
    });
    return (
      <div className={clsx(props.wrapperClassName)}>
        <label htmlFor={props.id} className={clsx(textMdSemibold, styles.label)}>
          {props.label}
        </label>
        <PhoneInput
          inputProps={{
            'aria-invalid': props.error ? 'true' : undefined,
            'aria-describedby': props.error ? `${props.id}-error` : undefined,
            id: props.id,
          }}
          forceDialCode={props.forceDialCode ?? true}
          {...props}
          inputRef={props.internalInputRef}
          ref={ref}
          value={props.value}
          className={clsx(props.className, styles.phoneInputContainer, props.error && styles.errorState)}
          countrySelectorStyleProps={{
            className: styles.phoneInputCountrySelector,
            buttonClassName: styles.phoneInputCountrySelectorButton,
            dropdownStyleProps: {
              className: styles.phoneInputCountrySelectorDropdown,
            },
          }}
          onChange={(phone, meta) => {
            // Get country code and phone without country code prefix
            const countryCode = meta.country?.dialCode;
            let phoneWithoutCountryCode = phone.replace(`+${countryCode}`, '');

            if (removeLeadingZero) {
              phoneWithoutCountryCode = phoneWithoutCountryCode.replace(/^0+/, '');
            }

            // Format and call parent onChange
            props.onChange?.(`+${countryCode}${phoneWithoutCountryCode}`, meta);
          }}
          inputClassName={clsx(styles.phoneInputField, props.inputClassName)}
          onBlur={(e) => {
            inputChangeEventTracker();
            props.onBlur?.(e);
          }}
        />
        {!hasNoBottomContent && (
          <div className={clsx(textMd, styles.bottom)}>
            {props.bottomHint && !props.error && <span className={styles.bottomHint}>{props.bottomHint}</span>}
            {props.error && (
              <p id={`${props.id}-error`} className={styles.errorMessage} role='alert'>
                {props.error}
              </p>
            )}
          </div>
        )}
      </div>
    );
  },
);
